import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { gql } from 'apollo-angular';
import { OuiAutocomplete, OuiAutocompleteOption } from 'omnium-ui/form-field';
import { QueryManagerService } from 'projects/box-lib/src/lib/services/queryManagerService';
import {
  Consultant,
  ConsultantFilterInput,
  ConsultantsPaginatedCollectionSegment,
} from 'projects/box-lib/src/lib/models/generated/graphql';
import { debounceTime, distinctUntilChanged, lastValueFrom, Subject, takeUntil } from 'rxjs';
const ALLCONSULTANTS = gql`
  query allConsultants($filter: ConsultantFilterInput, $searchText: String!) {
    consultantsByNamePaginated(skip: 0, take: 25, searchText: $searchText, where: $filter) {
      items {
        id
        personnePhysique {
          nom
          prenom
        }
      }
    }
  }
`;
@Component({
    selector: 'lib-search-consultant-autocomplete',
    templateUrl: './search-consultant-autocomplete.component.html',
    styleUrls: ['./search-consultant-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchConsultantAutocompleteComponent {
  @ViewChild('consultantAutocomplete') autocomplete: OuiAutocomplete<any>;
  @Input() label: string = 'Consultant';
  @Input() initConsultant: Consultant | undefined;

  @Input() consultantControl: FormControl = new FormControl<number | undefined>(undefined);
  consultantAutocompleteOptions: OuiAutocompleteOption[] = [];
  isConsultantLoading: boolean = false;

  @Output() onConsultantIdSelected = new EventEmitter<number | null | undefined>();
  @Output() onConsultantSelected = new EventEmitter<Consultant | undefined>();
  private ngUnsubscribe = new Subject<void>();
  consultants: Consultant[] = [];

  constructor(private queryManager: QueryManagerService) {}
  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
  ngOnInit() {
    if (this.initConsultant) {
      this.consultants = [this.initConsultant];
      this.setOptions();
    }
    this.listenForConsultantsChanges();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.initConsultant) {
        this.consultantControl.setValue(this.initConsultant.id);
      }
    });
  }

  listenForConsultantsChanges() {
    this.consultantControl.valueChanges
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntil(this.ngUnsubscribe))
      .subscribe(value => {
        if (Number.isInteger(value)) {
          const consultantFound = this.consultants.find(consultant => consultant.id == value);
          if (consultantFound) {
            this.onConsultantIdSelected.emit(consultantFound.id);
            this.onConsultantSelected.emit(consultantFound);
          }
          return;
        }
        this.onConsultantIdSelected.emit(null);
        this.onConsultantSelected.emit(undefined);
        if (value) {
          this.fetchConsultants(value);
          return;
        }
        this.fetchConsultants();
      });
  }

  async fetchConsultants(search: string = '') {
    this.isConsultantLoading = true;

    const queryResult = await lastValueFrom(
      this.queryManager.query<{ consultantsByNamePaginated: ConsultantsPaginatedCollectionSegment }>({
        query: ALLCONSULTANTS,
        variables: {
          filter: {} as ConsultantFilterInput,
          searchText: search,
        },
      })
    );

    this.consultants = queryResult.data.consultantsByNamePaginated.items ?? [];
    this.setOptions();
    this.isConsultantLoading = false;
  }

  setOptions() {
    this.consultantAutocompleteOptions = this.consultants.map(consultant => ({
      label: `${consultant.personnePhysique[0]?.nom} ${consultant.personnePhysique[0]?.prenom}`,
      value: consultant.id,
    }));
  }

  // use to set the autocomplete value outside this component
  setControlValue(consultantId: number | null | undefined) {
    if (consultantId) {
      this.consultantControl.setValue(consultantId);
    } else {
      this.consultantControl.setValue(null);
      this.consultantControl.reset();
      this.autocomplete.empty();
    }
    this.consultantControl.updateValueAndValidity();
  }
}
